import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Public, unauthenticated short-link redirector.
 *
 * Route: /drawdown/:code
 *
 * The actual streaming happens at `/api/short-links/drawdown/{code}`. We
 * prefer the same origin the investor arrived on (`window.location.origin`)
 * so a link sent as `https://prime-aif.com/drawdown/abc` stays on
 * `prime-aif.com` — the Emergent ingress forwards `/api/*` to the backend
 * pod transparently. Falling back to `REACT_APP_BACKEND_URL` is only used
 * for non-browser execution (unit tests, SSR previews) where
 * `window.location` isn't available.
 *
 * Using `window.location.replace` keeps the back button from looping
 * investors back to this loading shell.
 */
export default function DrawdownShortLink() {
  const { code } = useParams();
  const sameOrigin = (typeof window !== 'undefined' && window.location && window.location.origin) || '';
  const resolveOrigin = sameOrigin || BACKEND_URL;
  const initialError = !code
    ? 'Missing link code.'
    : !resolveOrigin
    ? 'Application is not configured to resolve this link.'
    : '';
  const [error] = useState(initialError);

  useEffect(() => {
    if (initialError) return;
    const target = `${resolveOrigin}/api/short-links/drawdown/${encodeURIComponent(code)}`;
    window.location.replace(target);
  }, [code, initialError, resolveOrigin]);

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0f172a',
        color: '#e2e8f0',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '24px',
      }}
      data-testid="drawdown-short-link-page"
    >
      <div style={{ textAlign: 'center', maxWidth: 480 }}>
        {error ? (
          <>
            <div style={{ fontSize: 22, fontWeight: 600, marginBottom: 12 }}>
              Link unavailable
            </div>
            <div style={{ fontSize: 14, color: '#94a3b8' }} data-testid="drawdown-short-link-error">
              {error}
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                width: 36,
                height: 36,
                margin: '0 auto 16px',
                border: '3px solid rgba(148, 163, 184, 0.35)',
                borderTopColor: '#38bdf8',
                borderRadius: '50%',
                animation: 'dd-spin 0.9s linear infinite',
              }}
            />
            <div style={{ fontSize: 16, fontWeight: 500 }}>Opening your drawdown notice…</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
              If this doesn’t redirect automatically,{' '}
              <a
                href={`${resolveOrigin}/api/short-links/drawdown/${encodeURIComponent(code || '')}`}
                style={{ color: '#38bdf8' }}
                data-testid="drawdown-short-link-fallback"
              >
                click here
              </a>
              .
            </div>
            <style>{`@keyframes dd-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </>
        )}
      </div>
    </div>
  );
}
